/**
 * The complete list of things usage counting may report — and nothing else.
 *
 * This file is the privacy guarantee. `recordFeature()` refuses any key that is
 * not in this frozen set, so a request URL, a body, a header, a collection name
 * or a vault entry cannot reach the wire even by mistake: there is no code path
 * that sends a caller-supplied string. Adding a feature means adding a constant
 * here, which is a reviewable diff rather than a call site nobody notices.
 *
 * Keys are stable identifiers, never display names — renaming a panel in the UI
 * must not silently split a counter in two on the server.
 *
 * What a counter means: "this build used this feature at least once on this UTC
 * day". It is not a click count and not a duration. That is deliberate — the
 * question being answered is "what is used and what is never touched", and a
 * once-per-day mark answers it without the volume telling you anything about an
 * individual's session.
 */

/** Panels opened from the icon rail. One per bundled extension. */
export const PANEL = Object.freeze({
  COLLECTIONS: 'panel.collections',
  HISTORY: 'panel.history',
  GIT: 'panel.git',
  TASK_MANAGER: 'panel.task-manager',
  DOCUMENTS: 'panel.documents',
  CHAT: 'panel.chat',
  VAULT: 'panel.vault',
  FTP: 'panel.ftp',
  TERMINAL: 'panel.terminal',
  CALENDAR: 'panel.calendar',
  MAIL_CATCHER: 'panel.mail-catcher',
  PROJECTS: 'panel.projects',
  GRAPH: 'panel.graph',
  DB_MANAGER: 'panel.db-manager',
});

/** A request of this kind was SENT (not merely opened as a tab). */
export const REQUEST = Object.freeze({
  HTTP: 'request.http',
  GRAPHQL: 'request.graphql',
  WEBSOCKET: 'request.websocket',
  SSE: 'request.sse',
  GRPC: 'request.grpc',
  /** An external agent (Claude Code / Desktop / Codex) made a successful MCP
   *  tool call — the only meaning of "MCP request": the API client has no MCP
   *  request type. Recorded in hooks/useMcpBus.js. */
  MCP: 'request.mcp',
});

/**
 * Everything else worth knowing is or is not used at all.
 *
 * A key belongs here ONLY once something calls `recordFeature` with it. A
 * declared-but-unwired key reads on the dashboard as a hard zero, and a zero
 * looks like a finding — "nobody imports anything, drop it" — when it actually
 * means "nobody wired the counter". Add the call site and the constant in the
 * same change, never the constant alone.
 *
 * Deliberately NOT here yet, for want of a call site: variable-create (no
 * single save funnel tells a new variable from an edited one).
 */
export const ACTION = Object.freeze({
  COLLECTION_RUN: 'action.collection-run',
  IMPORT: 'action.import',
  /** A collection/workspace was exported to a file (ExportCollectionModal). */
  EXPORT: 'action.export',
  /** A post-request (test) script ran: Send (RequestBuilder) or the runner. */
  TEST_SCRIPT: 'action.test-script',
  /** The Secrets Vault was actually unlocked (the gate, or the FTP panel). */
  VAULT_UNLOCK: 'action.vault-unlock',
  /** The MCP server is enabled — a state, counted once per app start. */
  MCP_SERVER_ON: 'action.mcp-server-on',
  /** A new message was sent in the AI assistant chat (not a retry). */
  AI_CHAT: 'action.ai-chat',
  /** A cloud workspace is registered with the sync engine. */
  SYNC_ON: 'action.sync-on',
  /** RETIRED (Plans v2, 15.10.2026: the ♥ Support button is gone). Kept so the
   *  key stays in the allow-list that old clients still send.
   *  Was: the title bar's "♥ Support" button was clicked
   *  — how many devices a day look at supporting Apiboo, not who paid. */
  SUPPORT_CLICK: 'action.support-click',
});

/**
 * Which MCP client called and which surface its tool belongs to (card
 * e1b83600). Noted in Rust for every `tools/call` (src-tauri/src/mcp/
 * usage_tally.rs — native tools never reach the JS bus) and drained into this
 * counter by `services/telemetry/mcpUsage.js`. Keys only: never a tool name.
 */
export const MCP = Object.freeze({
  CLIENT_CLAUDE_CODE: 'mcp.client.claude-code',
  CLIENT_CLAUDE_DESKTOP: 'mcp.client.claude-desktop',
  CLIENT_CODEX: 'mcp.client.codex',
  CLIENT_CURSOR: 'mcp.client.cursor',
  CLIENT_GEMINI: 'mcp.client.gemini',
  CLIENT_GROK: 'mcp.client.grok',
  CLIENT_OTHER: 'mcp.client.other',
  SURFACE_DOCUMENTS: 'mcp.surface.documents',
  SURFACE_MEMORY: 'mcp.surface.memory',
  SURFACE_BOARDS: 'mcp.surface.boards',
  SURFACE_CALENDAR: 'mcp.surface.calendar',
  SURFACE_COLLECTIONS: 'mcp.surface.collections',
  SURFACE_TERMINAL: 'mcp.surface.terminal',
  SURFACE_GRAPH: 'mcp.surface.graph',
  SURFACE_ATTACHMENTS: 'mcp.surface.attachments',
});

/**
 * Terminal (card e1b83600): which shell a tab or split opened, which coding
 * agent profile launched, splits and the MCP-approved AI tab. Recorded in
 * `services/telemetry/terminalUsage.js` from the terminal store / launcher.
 */
export const TERMINAL = Object.freeze({
  SHELL_CMD: 'terminal.shell.cmd',
  SHELL_POWERSHELL: 'terminal.shell.powershell',
  SHELL_PWSH: 'terminal.shell.pwsh',
  SHELL_GITBASH: 'terminal.shell.gitbash',
  SHELL_WSL: 'terminal.shell.wsl',
  SHELL_BASH: 'terminal.shell.bash',
  SHELL_ZSH: 'terminal.shell.zsh',
  SHELL_FISH: 'terminal.shell.fish',
  SHELL_SH: 'terminal.shell.sh',
  SHELL_SSH: 'terminal.shell.ssh',
  SHELL_OTHER: 'terminal.shell.other',
  AGENT_CLAUDE: 'terminal.agent.claude',
  AGENT_CODEX: 'terminal.agent.codex',
  AGENT_CURSOR: 'terminal.agent.cursor',
  AGENT_GROK: 'terminal.agent.grok',
  AGENT_GEMINI: 'terminal.agent.gemini',
  AGENT_CUSTOM: 'terminal.agent.custom',
  SPLIT: 'terminal.split',
  AI_TAB: 'terminal.ai-tab',
});

/**
 * Git (Track on this device, 04.10.2026): a workspace started being tracked
 * in a local repository, and a remote was added to such a history. Recorded
 * in hooks/useGitLocal.js on the success path only.
 */
export const GIT = Object.freeze({
  TRACK_LOCAL: 'git.track-local',
  ADD_REMOTE: 'git.add-remote',
});

/**
 * The account vault's cloud copy (Standalone vault card 7.2): how many devices
 * choose the cloud or "this device only", how many sync with it on a given day,
 * and how many hit a sync error — the enrolment health the rollout (7.3) waits
 * for. Keys only: never a vault id, an item count, a name or an error text.
 * Recorded in VaultCloudConsentDialog.js (the choice) and services/vaultSync.js
 * (`ensureVaultSync`: on / error).
 */
export const VAULT = Object.freeze({
  CLOUD_CHOSEN: 'vault.cloud-chosen',
  DEVICE_ONLY: 'vault.device-only',
  CLOUD_ON: 'vault.cloud-on',
  SYNC_ERROR: 'vault.sync-error',
});

/**
 * Every allowed key, flattened. `recordFeature()` checks membership here; a key
 * outside it is dropped and warned about in development, never sent.
 */
export const FEATURE_KEYS = Object.freeze(
  new Set([
    ...Object.values(PANEL),
    ...Object.values(REQUEST),
    ...Object.values(ACTION),
    ...Object.values(MCP),
    ...Object.values(TERMINAL),
    ...Object.values(GIT),
    ...Object.values(VAULT),
  ])
);

/** True when `key` is one this build is allowed to report. */
export const isFeatureKey = (key) => typeof key === 'string' && FEATURE_KEYS.has(key);

/**
 * The feature key for an icon-rail item, or null when it is not one we count.
 *
 * Rail items are either builtins (`collections`, `history`) or extension entries
 * whose id is `<extension>.activity`. Returning null rather than a guessed key
 * matters: a third-party extension installed from the marketplace would
 * otherwise mint a brand-new counter name, and the allow-list exists precisely
 * so that only keys chosen here can be sent.
 */
export function panelKeyFor(railItemId) {
  if (typeof railItemId !== 'string') return null;
  const key = `panel.${railItemId.replace(/\.activity$/, '')}`;
  return FEATURE_KEYS.has(key) ? key : null;
}

/**
 * The feature key for a request-kind option id (`utils/requestKinds.js`:
 * http · graphql · websocket · sse · grpc), or null.
 *
 * Counted when a request is SENT rather than when a tab is opened — an empty
 * gRPC tab nobody ran is not evidence that anyone uses gRPC, and "what is
 * actually used" is the whole point of the exercise.
 */
export function requestKeyFor(optionId) {
  if (typeof optionId !== 'string') return null;
  const key = `request.${optionId}`;
  return FEATURE_KEYS.has(key) ? key : null;
}

/** `terminal.shell.<key>` for a shell key from `terminal_list_shells`, or the `other` bucket. */
export function terminalShellKeyFor(shellKey) {
  const key = `terminal.shell.${shellKey}`;
  return typeof shellKey === 'string' && FEATURE_KEYS.has(key) ? key : TERMINAL.SHELL_OTHER;
}

/** `terminal.agent.<id>` for a built-in agent profile id, else `custom`. */
export function terminalAgentKeyFor(profileId) {
  const key = `terminal.agent.${profileId}`;
  return typeof profileId === 'string' && FEATURE_KEYS.has(key) && profileId !== 'custom'
    ? key
    : TERMINAL.AGENT_CUSTOM;
}
